import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Markdown } from "tiptap-markdown";
import { FaBold, FaItalic, FaListUl, FaLink, FaMagic } from "react-icons/fa";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import Loader from "@/components/loader";

export default function TiptapEditor({ 
  value, 
  onChange, 
  onBlur, 
  placeholder,
  // AI Assistant props
  aiEnabled = false,
  aiPrompt = "",
  onAiPromptChange,
  onAiSubmit,
  aiGenerating = false,
  aiPlaceholder = "Ask AI to write or improve your description...",
  aiQuickActions = [],
}) {
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Start writing...",
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline",
        },
      }),
      Markdown,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const markdown = jsonToMarkdown(json);
      onChange(markdown);
    },
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
  });

  // Update editor content when value changes externally (e.g., from AI generation)
  useEffect(() => {
    if (editor && value) {
      const currentMarkdown = jsonToMarkdown(editor.getJSON());
      if (value !== currentMarkdown) {
        editor.commands.setContent(value || "");
      }
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="tiptap-editor">
      {/* Toolbar */}
      <div className="tiptap-toolbar flex items-center justify-between">
        <div className="flex items-center">
          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`toolbar-button ${editor.isActive("bold") ? "is-active" : ""}`}
              title="Bold (Cmd+B)"
            >
              <FaBold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`toolbar-button ${editor.isActive("italic") ? "is-active" : ""}`}
              title="Italic (Cmd+I)"
            >
              <FaItalic className="w-4 h-4" />
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`toolbar-button ${editor.isActive("bulletList") ? "is-active" : ""}`}
              title="Bullet List"
            >
              <FaListUl className="w-4 h-4" />
            </button>
          </div>

          <div className="toolbar-divider" />

          <div className="toolbar-group">
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={addLink}
              className={`toolbar-button ${editor.isActive("link") ? "is-active" : ""}`}
              title="Add Link"
            >
              <FaLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* AI Assist Button */}
        {aiEnabled && (
          <button
            type="button"
            onClick={() => setAiPanelOpen(!aiPanelOpen)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              aiPanelOpen 
                ? "bg-primary text-white" 
                : "bg-primary/10 text-primary hover:bg-primary/20"
            }`}
          >
            <FaMagic className="w-3 h-3" />
            <span>AI Assist</span>
            {aiPanelOpen ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
          </button>
        )}
      </div>

      {/* AI Panel - Collapsible */}
      {aiEnabled && aiPanelOpen && (
        <div className="bg-gray-50 border-b border-gray-200 p-3">
          <div className="flex gap-2 items-start">
            <textarea
              placeholder={aiPlaceholder}
              value={aiPrompt}
              onChange={(e) => onAiPromptChange?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && aiPrompt.trim()) {
                  e.preventDefault();
                  onAiSubmit?.();
                }
              }}
              disabled={aiGenerating}
              rows={2}
              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
            />
            <button
              type="button"
              onClick={onAiSubmit}
              disabled={aiGenerating || !aiPrompt?.trim()}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
            >
              {aiGenerating ? <Loader size="small" color="white" /> : <FaMagic className="w-3 h-3" />}
              Generate
            </button>
          </div>
          {aiQuickActions.length > 0 && (
            <div className="flex gap-2 mt-2">
              {aiQuickActions.map((action, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => action.onClick?.()}
                  disabled={aiGenerating}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} className="tiptap-content" />
    </div>
  );
}

// Helper function to convert JSON to markdown with preserved empty lines
function jsonToMarkdown(json) {
  if (!json?.content) return "";

  const applyMarks = (text, marks) => {
    if (!marks) return text;
    return marks.reduce((txt, mark) => {
      const formats = {
        bold: (t) => `**${t}**`,
        italic: (t) => `*${t}*`,
        code: (t) => `\`${t}\``,
        link: (t) => `[${t}](${mark.attrs.href})`,
      };
      return formats[mark.type]?.(txt) || txt;
    }, text);
  };

  const processText = (content) => {
    return (
      content?.reduce((text, child) => {
        if (child.type === "text") return text + applyMarks(child.text || "", child.marks);
        if (child.type === "hardBreak") return text + "  \n";
        return text;
      }, "") || ""
    );
  };

  const processListItems = (listContent, prefix) => {
    return (
      listContent?.flatMap(
        (item, idx) =>
          item.content?.map((para) => (para.type === "paragraph" ? `${typeof prefix === "function" ? prefix(idx) : prefix}${processText(para.content)}` : null)).filter(Boolean) ||
          []
      ) || []
    );
  };

  const lines = json.content
    .map((node) => {
      if (node.type === "paragraph") {
        return !node.content?.length ? "&nbsp;" : processText(node.content);
      }
      if (node.type === "heading") {
        return `${"#".repeat(node.attrs?.level || 1)} ${processText(node.content)}`;
      }
      if (node.type === "bulletList") {
        return processListItems(node.content, "- ");
      }
      if (node.type === "orderedList") {
        return processListItems(node.content, (idx) => `${idx + 1}. `);
      }
      if (node.type === "blockquote") {
        return node.content?.map((para) => `> ${processText(para.content)}`);
      }
      if (node.type === "codeBlock") {
        return `\`\`\`\n${processText(node.content)}\n\`\`\``;
      }
      return null;
    })
    .flat()
    .filter(Boolean);

  return lines.join("\n\n");
}
